import { AuthApiClient, AuthApiRequestOptions } from './AuthApiClient';
import { AuthApiErrorResponse } from './AuthApiErrorResponse';
import {
    ActionName,
    ActionResult,
    ActionState,
    anonymousAuthState,
    AuthState,
    idleActionState,
    initializingAuthState,
    isSameAuthState,
    toAuthState,
} from './AuthState';

/*
 * Ported from the Next.js site's `src/auth-client/AuthClient.ts` (branch
 * feature/auth-client, PR leo-website#793). The port deviates only where this
 * repository forces or motivates it:
 *
 * - The sign-in flow surface (the session state, loadSession, the verify
 *   actions and updatePassword) is not ported: signing in happens on the
 *   Next.js site and this app only discovers the session, so only the
 *   authentication status, the access token plumbing and the sign-out remain.
 * - The requests go through the hand-written AuthApiClient instead of
 *   `openapi-fetch`, to not take on a new dependency in an application that
 *   is being retired.
 * - `AbortSignal.timeout` is missing from the TS 4.7 dom lib, so a helper
 *   builds the timeout signal from an AbortController.
 * - Token expiry is parsed with `Date.parse` instead of Temporal, to not pull
 *   `temporal-polyfill` into an application that is being retired — with an
 *   explicit NaN guard, since Date.parse reports a bad date as NaN where
 *   Temporal throws.
 * - Logging follows this repository's AuthService pattern (console, gated on
 *   NODE_ENV) instead of the source's opt-in trace flag.
 * - The Accept-Language value is a fixed string: the locale is derived from
 *   the domain and can never change without a page load.
 */

/**
 * Configuration for the auth client.
 */
export interface AuthClientConfiguration {
    /** Base url of the Auth API for the current country, without a trailing slash. */
    baseUrl: string;

    /** The Accept-Language header value, e.g. 'sv-SE'. */
    acceptLanguage: string;
}

/**
 * How often an authenticated session is re-checked while its tab is visible.
 * Doubles as the ceiling on the boot retry backoff.
 */
const REFRESH_INTERVAL_MS = 60 * 1000;

/**
 * Skip the re-check on becoming visible or coming back online if one ran this
 * recently.
 */
const REVALIDATE_STALE_AFTER_MS = 30 * 1000;

/** Treat an access token as expired this long before it actually expires. */
const ACCESS_TOKEN_EXPIRY_MARGIN_MS = 60 * 1000;

/**
 * Applied to every request. Without one a hung request leaves an action
 * running forever, which stops the polling and rejects every later action.
 */
const REQUEST_TIMEOUT_MS = 15 * 1000;

/** Attempts before the state falls back to anonymous. Retries continue after. */
const BOOT_MAX_ATTEMPTS = 3;
const BOOT_RETRY_BASE_DELAY_MS = 500;

/**
 * Attempts to mint an access token that survives to the caller. Only an
 * invalidation landing mid-flight costs an attempt, so this is generous.
 */
const MINT_MAX_ATTEMPTS = 3;

const HTTP_STATUS_UNAUTHORIZED = 401;

function isDocumentHidden(): boolean {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => {
        window.setTimeout(resolve, milliseconds);
    });
}

/**
 * Builds an aborting timeout signal. `AbortSignal.timeout` is unavailable in
 * the TypeScript 4.7 dom lib (and in older Safari), so it is assembled from an
 * AbortController instead.
 * @param milliseconds How long the signal stays live.
 * @returns A signal that aborts after the timeout.
 */
function createTimeoutSignal(milliseconds: number): AbortSignal {
    const controller = new AbortController();
    window.setTimeout(() => controller.abort(), milliseconds);
    return controller.signal;
}

/**
 * Handles the cookie based session against the Auth API.
 *
 * Owns the authentication state, which is loaded on start and polled, and is
 * what the application cares about. Signing in happens on the Next.js site;
 * this client only discovers the session, mints access tokens for it, and can
 * destroy it. The internal action state exists to keep actions from
 * overlapping.
 *
 * The constructor is side effect free; nothing is read from the browser and no
 * request is made until start() is called.
 */
export class AuthClient {
    private readonly api: AuthApiClient;

    private authState: AuthState = initializingAuthState;
    private actionState: ActionState = idleActionState;

    private readonly authListeners = new Set<() => void>();

    /**
     * Bumped whenever the authentication state actually changes. A request that
     * started before the current revision no longer describes the truth and is
     * discarded.
     */
    private revision = 0;

    private startCount = 0;
    private hasBooted = false;

    private timeoutId: number | null = null;
    private lastCheckedAtMs = 0;

    private accessToken: { value: string; expiresAtMs: number } | null = null;
    private accessTokenEpoch = 0;

    /** The epoch is kept so a flight that is already doomed is not joined. */
    private accessTokenRequest: {
        epoch: number;
        promise: Promise<string | null>;
    } | null = null;

    /** Event handlers bound to the correct "this" */
    private readonly boundOnVisibilityChange = this.onVisibilityChange.bind(this);
    private readonly boundOnOnline = this.onOnline.bind(this);
    private readonly boundOnPageShow = this.onPageShow.bind(this);

    constructor(configuration: AuthClientConfiguration) {
        this.api = new AuthApiClient({
            baseUrl: configuration.baseUrl,
            acceptLanguage: configuration.acceptLanguage,
        });
    }

    /* ---------------------------------------------------------------- state */

    getAuthState(): AuthState {
        return this.authState;
    }

    subscribeAuthState(listener: () => void): () => void {
        this.authListeners.add(listener);
        return () => {
            this.authListeners.delete(listener);
        };
    }

    /* ------------------------------------------------------------ lifecycle */

    /**
     * Starts monitoring the authentication status.
     *
     * Reference counted, so uncoordinated owners cannot boot the client twice
     * or leave listeners behind.
     * @returns A function that stops this particular start.
     */
    start(): () => void {
        this.startCount++;

        if (this.startCount === 1) {
            this.addEventListeners();

            if (this.hasBooted) {
                this.scheduleNextCheck();
            } else {
                this.hasBooted = true;
                void this.boot();
            }
        }

        let stopped = false;

        return () => {
            if (stopped) {
                return;
            }

            stopped = true;
            this.startCount--;

            if (this.startCount === 0) {
                this.removeEventListeners();
                this.clearTimer();
            }
        };
    }

    /* ---------------------------------------------------------------- actions */

    signOut(): Promise<ActionResult> {
        return this.runAction('signOut', async () => {
            await this.api.destroySession(this.createRequestOptions());

            this.setAuthState(anonymousAuthState);
        });
    }

    /* ----------------------------------------------------------- access token */

    /**
     * Returns an access token for calling other APIs, minting a new one when the
     * cached token is missing or about to expire.
     *
     * Resolves to null only while the session is not authenticated, so a caller
     * may read null as "anonymous" and send the request unauthenticated. While
     * the first check is still running that answer is not known yet, so this
     * waits for it rather than reading "initializing" as anonymous. When an
     * authenticated session cannot produce a token this throws instead: sending
     * that request without the token would answer as nobody.
     * @returns The token, or null while the session is not authenticated.
     */
    async getAccessToken(): Promise<string | null> {
        await this.waitForFirstCheck();

        for (let attempt = 1; attempt <= MINT_MAX_ATTEMPTS; attempt++) {
            if (this.authState.status !== 'authenticated') {
                return null;
            }

            const cached = this.accessToken;

            if (cached !== null && cached.expiresAtMs - ACCESS_TOKEN_EXPIRY_MARGIN_MS > Date.now()) {
                return cached.value;
            }

            const token = await this.mintAccessToken();

            if (token !== null) {
                return token;
            }

            this.log('info', `Access token ${attempt} went stale, minting again`);
        }

        throw new Error('Could not mint an access token; every attempt was invalidated in flight.');
    }

    /**
     * Drops the cached access token so the next call mints a new one. Call this
     * when an API rejects the token.
     */
    invalidateAccessToken(): void {
        this.accessToken = null;
        this.accessTokenEpoch++;
    }

    /* ---------------------------------------------------------------- private */

    /**
     * Loads the initial authentication status, retrying with backoff until it
     * succeeds.
     *
     * After the first few attempts the state falls back to anonymous so the
     * application is not stuck behind a preloader, but the retries keep going. A
     * flaky connection must not leave a signed-in user looking signed out for a
     * whole refresh interval.
     */
    private async boot(): Promise<void> {
        for (let attempt = 1; this.startCount > 0; attempt++) {
            try {
                await this.checkAuthentication();
                this.scheduleNextCheck();
                return;
            } catch (error: unknown) {
                this.log('warn', `Initial check ${attempt} failed`, error);
            }

            // A check triggered by an online or visibility event, or a sign-in
            // completing, may already have settled the state between attempts.
            // Falling back would clobber it and read as a sign-out downstream.
            if (attempt === BOOT_MAX_ATTEMPTS && this.authState.status === 'initializing') {
                this.log('warn', 'Continuing as anonymous until a check succeeds');
                this.setAuthState(anonymousAuthState);
            }

            await delay(Math.min(BOOT_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1), REFRESH_INTERVAL_MS));
        }

        // Stopped before any check succeeded. Left as booted, the next start would
        // only schedule a check, which an unauthenticated state never runs, and
        // the client would sit in "initializing" for good.
        this.hasBooted = false;
    }

    /* -------------------------------------------------------------- monitor */

    private addEventListeners(): void {
        if (typeof window === 'undefined') {
            return;
        }

        document.addEventListener('visibilitychange', this.boundOnVisibilityChange);
        window.addEventListener('online', this.boundOnOnline);
        window.addEventListener('pageshow', this.boundOnPageShow);
    }

    private removeEventListeners(): void {
        if (typeof window === 'undefined') {
            return;
        }

        document.removeEventListener('visibilitychange', this.boundOnVisibilityChange);
        window.removeEventListener('online', this.boundOnOnline);
        window.removeEventListener('pageshow', this.boundOnPageShow);
    }

    private clearTimer(): void {
        if (this.timeoutId !== null) {
            window.clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }

    /**
     * Queues the next check. Chained rather than an interval so a slow response
     * cannot stack up checks behind it.
     *
     * Only authenticated sessions are polled. Signing in happens on the Next.js
     * site, so an anonymous visitor has nothing to discover between page loads.
     * They still re-check when the tab is shown again, which covers signing in
     * from somewhere else.
     */
    private scheduleNextCheck(): void {
        this.clearTimer();

        if (this.startCount === 0 || isDocumentHidden() || this.authState.status !== 'authenticated') {
            return;
        }

        this.timeoutId = window.setTimeout(() => {
            this.timeoutId = null;

            void this.runCheck().finally(() => this.scheduleNextCheck());
        }, REFRESH_INTERVAL_MS);
    }

    private revalidate(force: boolean): void {
        if (!force && Date.now() - this.lastCheckedAtMs < REVALIDATE_STALE_AFTER_MS) {
            return;
        }

        void this.runCheck();
    }

    private async runCheck(): Promise<void> {
        // An action is a more recent truth than anything a check could report.
        if (this.actionState.pendingAction !== null) {
            return;
        }

        try {
            await this.checkAuthentication();
        } catch (error: unknown) {
            this.log('warn', 'Failed to refresh the authentication state', error);
        }
    }

    private async checkAuthentication(): Promise<void> {
        const revision = this.revision;

        const dto = await this.api.getSessionInfo(this.createRequestOptions());

        if (revision !== this.revision) {
            this.log('info', 'Discarding an authentication status that went stale');
            return;
        }

        this.lastCheckedAtMs = Date.now();
        this.setAuthState(toAuthState(dto));
    }

    /**
     * Runs an action, tracking it in the action state.
     *
     * A request failure is a result, not an exception: it lands in the resolved
     * result, so an event handler can call an action bare without leaving an
     * unhandled rejection behind.
     *
     * Starting an action while one is running still rejects — that is a double
     * click or a bug, not a request failure.
     * @param name The name of the action, tracked in the action state.
     * @param action The action body.
     * @returns The action result, carrying any error.
     */
    private async runAction(name: ActionName, action: () => Promise<void>): Promise<ActionResult> {
        const running = this.actionState.pendingAction;

        if (running !== null) {
            const message = `Cannot run "${name}" while "${running}" is running.`;

            this.log('warn', message);
            throw new Error(message);
        }

        this.actionState = Object.freeze({ pendingAction: name, actionError: null });
        this.clearTimer();

        this.log('info', `Running "${name}"`);

        let error: unknown = null;

        try {
            await action();
        } catch (caught: unknown) {
            error = caught;

            // Info, not warn: the error is surfaced through the result, so this is
            // a trace, not a client defect.
            this.log('info', `Action "${name}" failed`, caught);
        }

        this.actionState = Object.freeze({
            pendingAction: null,
            actionError: error === null ? null : { action: name, error },
        });

        this.scheduleNextCheck();

        return { error };
    }

    /**
     * Resolves once the auth state has left "initializing" — the first check
     * answered, or boot gave up and fell back to anonymous. A signed-in user's
     * request issued during boot must wait here; answering null would send it
     * out unauthenticated.
     */
    private async waitForFirstCheck(): Promise<void> {
        while (this.authState.status === 'initializing') {
            await new Promise<void>((resolve) => {
                const unsubscribe = this.subscribeAuthState(() => {
                    unsubscribe();
                    resolve();
                });
            });
        }
    }

    /**
     * Mints a token, single flight per epoch.
     * @returns The token, or null when an invalidation landed while it was in
     *   flight. It may belong to a session, or an impersonated identity, that is
     *   no longer current, so it cannot be handed out.
     */
    private async mintAccessToken(): Promise<string | null> {
        const epoch = this.accessTokenEpoch;

        // Only join a flight from the current epoch. An older one is already
        // doomed to be discarded, and joining it would inherit that null.
        if (this.accessTokenRequest?.epoch === epoch) {
            return await this.accessTokenRequest.promise;
        }

        const promise = this.api.createAccessToken(this.createRequestOptions()).then(
            (dto): string | null => {
                if (epoch !== this.accessTokenEpoch) {
                    return null;
                }

                const expiresAtMs = Date.parse(dto.expires);

                // Date.parse reports a bad date as NaN rather than throwing, and a NaN
                // expiry would fail the freshness check forever — a silent mint per
                // request instead of one loud error.
                if (Number.isNaN(expiresAtMs)) {
                    throw new Error(`Could not parse the access token expiry "${dto.expires}".`);
                }

                this.accessToken = {
                    value: dto.value,
                    expiresAtMs,
                };

                return dto.value;
            },
            (error: unknown): never => {
                // A 401 here is the server saying the session is gone — a revocation
                // this client has not heard of yet. Re-check now, so the state
                // converges immediately instead of a poll interval later, with every
                // backend request failing while the UI still shows a signed-in user.
                if (AuthApiErrorResponse.is(error) && error.statusCode === HTTP_STATUS_UNAUTHORIZED) {
                    void this.runCheck();
                }

                throw error;
            },
        );

        const request = { epoch, promise };
        this.accessTokenRequest = request;

        try {
            return await promise;
        } finally {
            if (this.accessTokenRequest === request) {
                this.accessTokenRequest = null;
            }
        }
    }

    /**
     * Creates the request options carrying the timeout.
     * @returns The options, with a fresh signal per request.
     */
    private createRequestOptions(): AuthApiRequestOptions {
        return { signal: createTimeoutSignal(REQUEST_TIMEOUT_MS) };
    }

    /**
     * Updates the authentication state.
     * @param next The new state.
     * @returns Whether the state actually changed.
     */
    private setAuthState(next: AuthState): boolean {
        if (isSameAuthState(this.authState, next)) {
            return false;
        }

        const previous = this.authState;

        this.authState = Object.freeze(next);
        this.revision++;

        // A token belongs to one identity, so it survives neither signing out,
        // a different user signing in from another tab, nor switching which user
        // is impersonated — the user id is the effective user's.
        if (next.status !== 'authenticated' || previous.status !== 'authenticated' || previous.userId !== next.userId) {
            this.invalidateAccessToken();
        }

        this.log('info', 'Authentication state changed', next);
        this.notify(this.authListeners);

        // Starts polling on sign-in and stops it on sign-out.
        this.scheduleNextCheck();

        return true;
    }

    private notify(listeners: Set<() => void>): void {
        // Copied, as a listener is free to unsubscribe while being notified.
        for (const listener of [...listeners]) {
            try {
                listener();
            } catch (error: unknown) {
                // Isolated, so one throwing subscriber cannot leave the rest of the
                // application on a state it never heard changed.
                this.log('warn', 'A listener threw', error);
            }
        }
    }

    /**
     * Callback when the tab's visibility changes. Pauses the monitor while the
     * tab is hidden, and re-checks when it is shown again.
     */
    private onVisibilityChange(): void {
        if (isDocumentHidden()) {
            this.clearTimer();
            return;
        }

        this.revalidate(false);
        this.scheduleNextCheck();
    }

    /**
     * Callback when the network comes back.
     */
    private onOnline(): void {
        this.revalidate(false);
    }

    /**
     * Callback when a page is shown. A page restored from the back/forward
     * cache can be arbitrarily old, so its authentication status is always
     * worth re-checking.
     * @param event The page transition event.
     */
    private onPageShow(event: PageTransitionEvent): void {
        if (event.persisted) {
            this.revalidate(true);
        }
    }

    /**
     * Log to the console.
     */
    private log(severity: 'info' | 'warn', ...params: unknown[]): void {
        if (process.env.NODE_ENV !== 'development') {
            return;
        }

        if (severity === 'info') {
            console.info(`[${AuthClient.name}]:`, ...params);
        } else {
            console.warn(`[${AuthClient.name}]:`, ...params);
        }
    }
}
